/**
 * Live stats for the home page tiles and the Central Ideas row. One cached BFF call (the server holds a
 * 5-minute TTL as well); tiles render immediately and the stat lines appear
 * when data lands. Errors render nothing: a stats hiccup must never break
 * the home page.
 */

import { useQuery } from '@tanstack/react-query';
import { bffFetch } from '@shared/api/bffFetch';

export interface LauncherStats {
  /** `activeGroups` is the vba_isactive count, NOT a date-driven "in force"
   *  count. Label it "active groups"; the Ops dashboard's In Force stat asks
   *  about plan-year windows and reports a different, also-correct number. */
  ops: { activeGroups: number | null; eeLives: number | null };
  ideas: {
    /** Not shipped, not declined. The home panel's Ideas row. */
    openIdeas: number | null;
    shippedThisYear: number | null;
    year: number;
  };
  portfolio: { activeInitiatives: number | null };
}

export function useLauncherStats() {
  return useQuery({
    queryKey: ['launcher', 'stats'],
    staleTime: 5 * 60_000,
    retry: 1,
    queryFn: async (): Promise<LauncherStats> => {
      const res = await bffFetch('/api/launcher/stats');
      if (!res.ok) throw new Error(`Launcher stats failed (${res.status})`);
      return ((await res.json()) as { stats: LauncherStats }).stats;
    },
  });
}

const plural = (n: number, word: string) => `${n.toLocaleString()} ${word}${n === 1 ? '' : 's'}`;

/** The one-line live stat for a module tile, or null when there is none. */
export function statLineFor(moduleId: string, stats: LauncherStats | undefined): string | null {
  if (!stats) return null;
  if (moduleId === 'ops') {
    const parts: string[] = [];
    if (typeof stats.ops.activeGroups === 'number') {
      parts.push(plural(stats.ops.activeGroups, 'active group'));
    }
    if (stats.ops.eeLives !== null && stats.ops.eeLives > 0) {
      parts.push(`${stats.ops.eeLives.toLocaleString()} EE lives`);
    }
    return parts.length > 0 ? parts.join(' · ') : null;
  }
  if (moduleId === 'portfolio') {
    return stats.portfolio.activeInitiatives !== null
      ? plural(stats.portfolio.activeInitiatives, 'active initiative')
      : null;
  }
  return null;
}
